import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import axios from "axios";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import SelectSearchInput from "../SelectSearch";
import { formatSalesValue } from "../../utils/formatUtils";
import * as XLSX from 'xlsx';
import { useNavigate } from "react-router-dom";
import InventoryHistoryTable from "./InventoryHistoryTable";
import InventoryHistoryChart from "./InventoryHistoryChart";

const InventoryHistory = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const navigate = useNavigate('');

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [reportDetails, setReportDetails] = useState({});
    const [error, setError] = useState(null);
    const [expandedReports, setExpandedReports] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState({});

    useEffect(() => {
        if (token && selectedOutlet) {
            fetchReportData();
        }
    }, [token, selectedOutlet]);

    const fetchReportData = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/inventory-snapshot-reports`, {
                token: token,
                outlet: selectedOutlet
            });
            const data = response.data;
            setReportData(data);
            setError(null);
        } catch (err) {
            setError(err.message);
            setReportData([]);
            toast.error("Failed to fetch report data");
        } finally {
            setLoading(false);
        }
    };

    const fetchReportDetails = async (reportId) => {
        try {
            setLoadingDetails(prev => ({ ...prev, [reportId]: true }));
            const response = await axios.post(`${baseUrl}/inventory-snapshot-reports-details`, {
                token: token,
                outlet: selectedOutlet,
                snapshot_id: reportId
            });
            const data = response.data;
            setReportDetails(prev => ({ ...prev, [reportId]: data }));
            setError(null);
        } catch (err) {
            setError(err.message);
            setReportDetails(prev => ({ ...prev, [reportId]: null }));
            toast.error("Failed to fetch report details");
        } finally {
            setLoadingDetails(prev => ({ ...prev, [reportId]: false }));
        }
    };

    const toggleReportExpansion = (reportId) => {
        if (expandedReports.includes(reportId)) {
            setExpandedReports(expandedReports.filter(id => id !== reportId));
        } else {
            setExpandedReports([...expandedReports, reportId]);
            if (!reportDetails[reportId]) {
                fetchReportDetails(reportId);
            }
        }
    };

    const formattedDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);

        const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
        const day = date.getDate();
        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return `${n}th`;
            switch (n % 10) {
                case 1: return `${n}st`;
                case 2: return `${n}nd`;
                case 3: return `${n}rd`;
                default: return `${n}th`;
            }
        };
        const dayWithOrdinal = getOrdinal(day);
        const month = date.toLocaleDateString("en-US", { month: "long" });
        const year = date.getFullYear();

        return `${weekday}, ${dayWithOrdinal} ${month}, ${year}`;
    };

    const handleCostingNavigation = async (id) => {
        navigate('/costinganalysis', {
            state: {
                snapshot_id: id
            }
        });
    }

    const groupItemsByType = (items) => {
        if (!items) return {};

        return items.reduce((acc, item) => {
            const type = item.item_type || 'Other';
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(item);
            return acc;
        }, {});
    };

    const handleExport = (report, groupedItemsByType) => {
        const workbook = XLSX.utils.book_new();

        const formattedMeta = [
            [`Inventory Record From ${formattedDate(report.start_date)} to ${formattedDate(report.end_date)}`],
            [],
            ['Created At:', formattedDate(report.created_at)],
            ['Start Date:', formattedDate(report.start_date)],
            ['End Date:', formattedDate(report.end_date)],
            ['Food Sold:', report.food_total_items || 0],
            ['Food Accuracy:', `${report.food_accuracy || 0}%`],
            ['Beverage Sold:', report.beverage_total_items || 0],
            ['Beverage Accuracy:', `${report.beverage_accuracy || 0}%`],
            [],
        ];

        const headers = [
            "Group",
            "Item Name",
            "Rate",
            "UOM",
            "Opening",
            "Received",
            "Wastage",
            "Consumption",
            "Closing",
            "Discrepancy",
            "Closing Amount"
        ];

        const colWidths = [
            { wch: 15 },
            { wch: 30 },
            { wch: 12 },
            { wch: 10 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 15 },
        ];

        Object.entries(groupedItemsByType).forEach(([type, items]) => {
            if (!items?.length) return;

            const dataRows = items.map(item => [
                item.group_name,
                item.item_name,
                formatSalesValue(item.rate),
                item.uom,
                item.opening_count,
                item.total_received,
                item.wastage_count,
                item.total_consumed,
                item.closing_balance,
                item.discrepancy_count,
                formatSalesValue(item.physical_cost),
            ]);

            const sheetData = [...formattedMeta, headers, ...dataRows];
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            worksheet['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(workbook, worksheet, type);
        });

        XLSX.writeFile(
            workbook,
            `Inventory_Report_${formattedDate(report.start_date)}_to_${formattedDate(report.end_date)}.xlsx`
        );
    };

    return (
        <div className='flex flex-col min-h-screen'>
            <Navbar />

            <div className="flex-grow">
                <div className="px-20 my-5">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    {loading && (
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger text-center">
                            {error}
                        </div>
                    )}

                    <InventoryHistoryTable
                        reportData={reportData}
                        reportDetails={reportDetails}
                        loading={loading}
                        loadingDetails={loadingDetails}
                        expandedReports={expandedReports}
                        groupItemsByType={groupItemsByType}
                        toggleReportExpansion={toggleReportExpansion}
                        formattedDate={formattedDate}
                        handleCostingNavigation={handleCostingNavigation}
                        handleExport={handleExport}
                        token={token}
                        fetchReportData={fetchReportData}
                        setExpandedReports={setExpandedReports}
                        setReportDetails={setReportDetails}
                    />

                    <InventoryHistoryChart outlet={selectedOutlet} />
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default InventoryHistory;